class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

async function addLymaCartPageCrossSell(button) {
  if (button.disabled) return;
  const variantId = Number(button.dataset.variantId || 0);
  if (!variantId) return;

  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  try {
    const addResponse = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    });
    const state = await addResponse.json();
    if (!addResponse.ok || state.status) throw new Error(state.description || state.message || 'Product kon niet worden toegevoegd.');

    const sectionResponse = await fetch(
      `${window.routes?.cart_url || '/cart'}?sections=main-cart-items,main-cart-footer,cart-icon-bubble&sections_url=${encodeURIComponent(window.location.pathname)}`,
      { headers: { Accept: 'application/json' } }
    );
    const sections = await sectionResponse.json();
    if (!sectionResponse.ok || !sections['main-cart-items'] || !sections['main-cart-footer']) throw new Error('Cartpagina kon niet worden vernieuwd.');

    const replaceSectionContents = (sectionId, targetSelector) => {
      const target = document.querySelector(targetSelector);
      const source = new DOMParser().parseFromString(sections[sectionId], 'text/html').querySelector('.js-contents');
      if (target && source) target.replaceWith(source);
    };
    replaceSectionContents('main-cart-items', '#main-cart-items .js-contents');
    replaceSectionContents('main-cart-footer', '#main-cart-footer .js-contents');

    const iconTarget = document.getElementById('cart-icon-bubble');
    const iconSource = new DOMParser().parseFromString(sections['cart-icon-bubble'] || '', 'text/html').querySelector('.shopify-section');
    if (iconTarget && iconSource) iconTarget.innerHTML = iconSource.innerHTML;
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: state } }));
  } catch (error) {
    console.error('Lyma cart page cross-sell add error:', error);
  } finally {
    button.disabled = false;
    button.removeAttribute('aria-busy');
  }
}

if (!window.lymaCrossSellDelegated) {
  window.lymaCrossSellDelegated = true;
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target.closest?.('product-form .lyma-cart-cross-sell__form');
      const productForm = form?.closest('product-form');
      if (!form || !productForm || typeof productForm.onSubmitHandler !== 'function') return;

      event.preventDefault();
      event.stopImmediatePropagation();
      productForm.onSubmitHandler(event);
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const addButton = event.target.closest?.('[data-lyma-cart-cross-sell-add]');
      const cartPage = addButton?.closest('#main-cart-footer');
      if (!addButton || !cartPage) return;

      event.preventDefault();
      event.stopPropagation();
      addLymaCartPageCrossSell(addButton);
    },
    true
  );
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
